import { useEffect, useRef, useState } from 'react'
import i18n, { type Resource } from 'i18next'
import { initReactI18next } from 'react-i18next'
import otaClient from '@crowdin/ota-client'
import { queryParser } from '@applyft-web/utils'
import { setSentryAppLocale } from '@analytics/sentry'
import { CROWDIN_DIST, ENV } from '@constants'
import {
  getLocaleFromPath,
  normalizeLocale,
  setSupportedLocalesFromCrowdin,
  type SupportedLocale,
} from '@utils'
import en from './en.json'

// types
type LocalesMap = Record<string, string>
type TranslationsCache = { ts: number; files: Record<string, object> }
interface UseInitLocalesProps {
  ready: boolean
  isRtl: boolean
  language: string | undefined
}

// constants
const FALLBACK = 'en'
const LS_KEY_TRANSLATIONS = 'i18n.translations'
const client = CROWDIN_DIST ? new otaClient(CROWDIN_DIST) : null
const inflightRes = new Map<string, Promise<object>>()
let localesCache: Promise<LocalesMap> | null = null

// utils
function resolveLang(requested: string, localesList: string[]): string {
  const { custom_lng } = queryParser(window.location.search)
  if (ENV !== 'production' && custom_lng) return custom_lng
  const normalize = (lng: string) => lng.replace('_', '-').toLowerCase() // e.g. en_US -> en-us
  const baseOf = (lng: string) => normalize(lng).split('-')[0]
  const req = normalize(requested)
  const available = localesList.map(normalize)

  const exactIdx = available.indexOf(req)
  if (exactIdx >= 0) return localesList[exactIdx]

  // e.g. "en-us" → "en"
  const base = baseOf(req)
  const baseIdx = available.indexOf(base)
  if (baseIdx >= 0) return localesList[baseIdx]

  // e.g. non-exist "en-US" → "en-UK"
  const variantIdx = available.findIndex((k) => baseOf(k) === base)
  if (variantIdx >= 0) return localesList[variantIdx]

  return FALLBACK
}
function readLS<T>(key: string): T | null {
  if (typeof window === 'undefined') return null
  try {
    return JSON.parse(localStorage.getItem(key) || 'null')
  } catch {
    return null
  }
}
function writeLS<T>(key: string, value: T) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* ignore quota */
  }
}

function applyDocumentLang(lng: string) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng
  }
  setSentryAppLocale(lng)
}

// requests
async function getLocalesList(): Promise<LocalesMap> {
  if (!client) return {}
  if (localesCache) return localesCache

  localesCache = client
    .getContent()
    .then((content) => {
      if (!content || typeof content !== 'object' || Array.isArray(content)) {
        return {}
      }
      return Object.fromEntries(Object.entries(content).map(([lng, urls]) => [lng, urls[0]]))
    })
    .catch((error) => {
      console.error(error)
      return {}
    })

  return localesCache
}
async function getRes(file?: string) {
  if (!file || !client) return {}

  try {
    const ts = await client.getManifestTimestamp()

    const cached = readLS<TranslationsCache>(LS_KEY_TRANSLATIONS)
    if (cached && cached.ts === ts && cached.files[file]) {
      return cached.files[file]
    }

    const p = inflightRes.get(file)
    if (p) return p

    const req = client
      .getFileTranslations(file)
      .then((tr) => (tr && typeof tr === 'object' ? tr : {}))
      .catch((e) => {
        console.error(e)
        return {}
      })
      .finally(() => inflightRes.delete(file))

    inflightRes.set(file, req)

    const translations = await req

    const base = readLS<TranslationsCache>(LS_KEY_TRANSLATIONS)
    const files = base && base.ts === ts ? base.files : {}
    writeLS<TranslationsCache>(LS_KEY_TRANSLATIONS, {
      ts,
      files: { ...files, [file]: translations },
    })

    return translations
  } catch (e) {
    console.error(e)
    const cached = readLS<TranslationsCache>(LS_KEY_TRANSLATIONS)
    return cached?.files?.[file] ?? {}
  }
}

async function ensureLanguage(requested: string): Promise<string> {
  const content = await getLocalesList()
  const localesList = Object.keys(content)
  setSupportedLocalesFromCrowdin(localesList)
  const target = localesList.length ? resolveLang(requested, localesList) : normalizeLocale(requested)
  const resByTarget = await getRes(content[target])
  const hasTargetRes = target !== FALLBACK && Object.keys(resByTarget).length > 0
  const active = hasTargetRes ? target : FALLBACK

  if (!i18n.isInitialized) {
    const startResources: Resource = {
      en: { translation: en },
    }
    if (hasTargetRes) {
      startResources[target] = { translation: resByTarget }
    }
    await i18n.use(initReactI18next).init({
      fallbackLng: FALLBACK,
      lng: active,
      resources: startResources,
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
      debug: ENV !== 'production',
    })
  } else {
    if (hasTargetRes && !i18n.hasResourceBundle(target, 'translation')) {
      i18n.addResourceBundle(target, 'translation', resByTarget, true, false)
    }
    if (i18n.language !== active) {
      await i18n.changeLanguage(active)
    }
  }

  applyDocumentLang(i18n.language ?? FALLBACK)
  return i18n.language ?? FALLBACK
}

export async function changeAppLanguage(locale: SupportedLocale): Promise<void> {
  await ensureLanguage(locale)
}

export function useInitLocales(): UseInitLocalesProps {
  const [language, setLanguage] = useState<string | undefined>()
  const [isRtl, setIsRtl] = useState(false)
  const [ready, setReady] = useState(false)
  const [content, setContent] = useState<Record<string, string> | undefined>()
  const didRun = useRef(false)

  useEffect(() => {
    const syncLanguage = (lng: string) => {
      setIsRtl(i18n.dir(lng) === 'rtl')
      setLanguage(lng)
      applyDocumentLang(lng)
    }

    i18n.on('languageChanged', syncLanguage)

    return () => {
      i18n.off('languageChanged', syncLanguage)
    }
  }, [])

  useEffect(() => {
    if (content) return

    let aborted = false
    getLocalesList().then((languages) => {
      if (aborted) return
      setSupportedLocalesFromCrowdin(Object.keys(languages))
      setContent(languages)
    })

    return () => {
      aborted = true
    }
  }, [content])

  useEffect(() => {
    if (didRun.current || !content) return
    didRun.current = true

    let aborted = false

    ;(async () => {
      const fromPath = getLocaleFromPath(window.location.pathname)
      const raw = fromPath ?? (typeof navigator !== 'undefined' ? navigator.language : FALLBACK)
      const active = await ensureLanguage(raw)

      if (aborted) return

      setIsRtl(i18n.dir(active) === 'rtl')
      setLanguage(active)
      setReady(true)
    })().catch((err) => {
      if (ENV !== 'production') console.error('i18n init failed:', err)
      if (!aborted) setReady(true)
    })

    return () => {
      aborted = true
    }
  }, [content])

  return { ready, isRtl, language }
}
